import { config } from '../config';
import { tokenStorage } from '../auth/tokenStorage';
import { refreshAccessToken } from '../auth/refresh';

class HttpError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean; // default true
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, signal } = opts;

  const buildHeaders = (token: string | null): HeadersInit => {
    const h: Record<string, string> = {};
    if (body !== undefined) h['Content-Type'] = 'application/json';
    if (auth && token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const send = async (token: string | null): Promise<Response> => {
    const init: RequestInit = {
      method,
      headers: buildHeaders(token),
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (signal) init.signal = signal;
    return fetch(`${config.apiBaseUrl}${path}`, init);
  };

  let token = auth ? tokenStorage.getAccess() : null;
  let resp = await send(token);

  if (resp.status === 401 && auth) {
    // refreshAccessToken owns the sign-out decision (it signs out only on a
    // definitive 401 / missing token, never on a transient failure). Here we
    // just retry on success or surface the 401 on failure.
    const newToken = await refreshAccessToken();
    if (newToken) {
      token = newToken;
      resp = await send(token);
    } else {
      throw new HttpError(401, null, 'Unauthorized');
    }
  }

  if (!resp.ok) {
    let errBody: unknown = null;
    try {
      errBody = await resp.json();
    } catch {
      // ignore
    }
    throw new HttpError(resp.status, errBody, `HTTP ${resp.status}`);
  }

  return (await resp.json()) as T;
}

export { HttpError };
